import {noop} from '@benchling/js-utils';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {OligoAwareFolderItemType} from 'constants/RegistryConstants';
import UserConstants from 'constants/UserConstants';
import {count} from 'metrics/Metrics';
import preferences from 'Preferences';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import Modal from 'ui-library/modals/Modal';
import {textToBlob} from 'util/TextToBlobUtil';
import {TextAreaChangeEvent} from 'util/Types';

import ModalButtonKey from '../../test/lib/cypress/shared/ModalButtonKey';
import * as SpreadsheetBulkImportActions from '../SpreadsheetBulkImportActions';
import {
  DEFAULT_IMPORT_SOURCE_TYPE,
  EXCEL_FILE_EXTENSIONS,
  IMPORT_TYPE,
  ImportSourceType,
  OPTIONS,
  RAW_TEXT_SPREADSHEET_NAME,
  SelectedSchemaIds,
  TaskState,
} from '../SpreadsheetBulkImportConstants';
import ImportSourceTypeSelector from './ImportSourceTypeSelector';
import {SpreadsheetLocaleSelector} from './SpreadsheetLocaleSelector';
import UploadRawTextForm from './UploadRawTextForm';
import UploadSDFForm from './UploadSDFForm';
import UploadSpreadsheetForm from './UploadSpreadsheetForm';

const {SPREADSHEET_IMPORT_LANGUAGE_TAG} = UserConstants;

export type GetExampleImageURLFunction = (args: {selectedSchemaIds: SelectedSchemaIds}) => string;
export type onUploadAnalyticsCallbackFn = (
  entityType: OligoAwareFolderItemType | null | undefined,
  uploadType: ImportSourceType,
  errorMessage?: string | undefined
) => void;

interface Props {
  errorMessage?: string | null;
  file?: File | null;
  getExampleImageURL: GetExampleImageURLFunction;
  onUploadSuccessAnalyticsCallback: onUploadAnalyticsCallbackFn;
  onUploadFailAnalyticsCallback: onUploadAnalyticsCallbackFn;
  importEntityType?: OligoAwareFolderItemType | null;
  importItemWord: string;
  importSourceType: ImportSourceType;
  importType: IMPORT_TYPE;
  isFirstPane: boolean;
  modalProps: {
    title: string;
    onRequestClose: () => void;
  };
  options: {[key in OPTIONS]: boolean};
  rawText: string;
  selectedSchemaIds: SelectedSchemaIds;
  uploadState?: TaskState | null;
  languageTag: string;
  // The maximum number of rows can be imported to a table without exceeding the table row limit. When this value gets passed in, we validate the row count on spreadsheet upload.
  maxAllowedRowsToImport?: number;
}

interface UploadSpreadsheetModalState {
  languageTag: string;
  isSettingLanguageTag: boolean;
  isUploadingExcel: boolean;
}

export interface UploadSpreadsheetModalPaneConfiguration {
  getExampleImageURL: GetExampleImageURLFunction;
  onUploadSuccessAnalyticsCallback: onUploadAnalyticsCallbackFn;
  onUploadFailAnalyticsCallback: onUploadAnalyticsCallbackFn;
  hideBackButton: boolean;
}

class UploadSpreadsheetModalPane extends React.Component<Props, UploadSpreadsheetModalState> {
  /**
   * Returns configuration for UploadSpreadsheetModalPane for a given import flow.
   * @param {getImageExampleURL}    Called with (extraState) and should return an image URL of an example
   *                                spreadsheet. Defaults to '/static/img/spreadsheet-import-example.png'.
   */

  constructor(props: Props) {
    super(props);
    this.state = {
      languageTag: this.props.languageTag,
      isSettingLanguageTag: false,
      isUploadingExcel: false,
    };
  }

  static configure({
    getExampleImageURL = () => '/static/img/spreadsheet-import-example.png',
    onUploadSuccessAnalyticsCallback = noop,
    onUploadFailAnalyticsCallback = noop,
    hideBackButton = false,
  }: {
    getExampleImageURL?: GetExampleImageURLFunction;
    onUploadSuccessAnalyticsCallback?: onUploadAnalyticsCallbackFn;
    onUploadFailAnalyticsCallback?: onUploadAnalyticsCallbackFn;
    hideBackButton?: boolean;
  } = {}): UploadSpreadsheetModalPaneConfiguration {
    return {
      getExampleImageURL,
      onUploadSuccessAnalyticsCallback,
      onUploadFailAnalyticsCallback,
      hideBackButton,
    };
  }

  private getExampleImageURL = (): string =>
    this.props.getExampleImageURL({selectedSchemaIds: this.props.selectedSchemaIds});

  private filenameImpliesExcel = (name: string): boolean =>
    EXCEL_FILE_EXTENSIONS.some((extension) => name.endsWith(extension));

  private handleChangeImportSource = (importSourceType: ImportSourceType): void => {
    SpreadsheetBulkImportActions.changeImportSourceType(importSourceType);
  };

  private handleChangeSpreadsheetLocale = (languageTag: string): void => {
    if (this.state.languageTag !== languageTag) {
      count('bnch.localization.spreadsheet_locale_updated_count');
    }
    this.setState({languageTag, isSettingLanguageTag: true});
    preferences.set(SPREADSHEET_IMPORT_LANGUAGE_TAG, languageTag).then(() => {
      this.setState({isSettingLanguageTag: false});
    });
  };

  // Tech debt: not private because used in tests
  _handleRemoveSpreadsheet = (): void => {
    SpreadsheetBulkImportActions.removeSpreadsheet();
  };

  // Tech debt: not private because used in tests
  _handleUploadAndParseSpreadsheet = (file: File): void => {
    SpreadsheetBulkImportActions.uploadAndParse(
      file,
      {
        onSuccessAnalyticsCallback: () => {
          this.props.onUploadSuccessAnalyticsCallback(
            this.props.importEntityType,
            this.props.importSourceType
          );
          this.setState({isUploadingExcel: this.filenameImpliesExcel(file.name)});
        },
        onFailAnalyticsCallback: (errorMessage: string) => {
          this.props.onUploadFailAnalyticsCallback(
            this.props.importEntityType,
            this.props.importSourceType,
            errorMessage
          );
        },
      },
      this.props.maxAllowedRowsToImport
    );
  };

  // Tech debt: not private because used in tests
  _handleUploadAndParseSDF = (file: File): void => {
    SpreadsheetBulkImportActions.uploadAndParseSDF(file);
  };

  private handleOnTextAreaChange = (e: TextAreaChangeEvent): void => {
    SpreadsheetBulkImportActions.updateRawText(e.target.value);
  };

  // Tech debt: not private because used in tests
  _handleClickNextButton = (): void => {
    const sourceType = this.props.importSourceType;
    if (sourceType === ImportSourceType.RAW_TEXT) {
      const blob: Blob & {name?: string} = textToBlob(this.props.rawText);
      blob.name = RAW_TEXT_SPREADSHEET_NAME;
      const file = new File([blob], blob.name, {
        type: blob.type,
      });
      SpreadsheetBulkImportActions.uploadAndParse(file, {
        onSuccessAnalyticsCallback: () => {
          this.props.onUploadSuccessAnalyticsCallback(
            this.props.importEntityType,
            this.props.importSourceType
          );
        },
        onFailAnalyticsCallback: (errorMessage: string) => {
          this.props.onUploadFailAnalyticsCallback(
            this.props.importEntityType,
            this.props.importSourceType,
            errorMessage
          );
        },
      });
    }
    SpreadsheetBulkImportActions.goToNextPane();
  };

  private excelSpecificHelpText = (): JSX.Element => (
    <InlineBanner
      bannerType={InlineBannerType.INFO}
      text="Numbers and dates in .xlsx files will be imported from raw values."
      supportingInfo='These can be imported safely regardless of their display format, so long as they are stored as a "number" or "date" in excel'
    />
  );

  private spreadsheetLocaleSelectorOrText = (): JSX.Element => {
    if (this.props.importSourceType === ImportSourceType.SPREADSHEET) {
      if (this.props.uploadState !== TaskState.COMPLETED) {
        return <></>;
      } else if (this.state.isUploadingExcel) {
        return this.excelSpecificHelpText();
      }
    }

    return (
      <SpreadsheetLocaleSelector
        languageTag={this.state.languageTag}
        onChange={this.handleChangeSpreadsheetLocale}
      />
    );
  };

  render(): JSX.Element {
    const isSdfAllowed = this.props.importEntityType === ModelTypeConstants.MOLECULE;
    const shouldNextButtonBeDisabled =
      (!this.props.rawText && this.props.uploadState !== TaskState.COMPLETED) ||
      this.state.isSettingLanguageTag;
    // If SDF is the last saved import source in the spreadsheet store, we need to switch off from it
    // in settings where it should not be allowed.
    if (this.props.importSourceType === ImportSourceType.SDF && !isSdfAllowed) {
      SpreadsheetBulkImportActions.changeImportSourceType(DEFAULT_IMPORT_SOURCE_TYPE);
    }
    return (
      <Modal
        enableRedesign={false}
        buttons={_.compact([
          !this.props.isFirstPane && {
            key: 'back',
            text: 'Back',
            onClick: SpreadsheetBulkImportActions.goToPrevPane,
          },
          {
            key: 'next',
            text: this.props.uploadState === TaskState.STARTED ? 'Loading...' : 'Next',
            style: ButtonStyle.SECONDARY,
            onClick: this._handleClickNextButton,
            disabled: shouldNextButtonBeDisabled,
            dataTestAttrs: {
              component: 'UploadSpreadsheetModal',
              element: 'footerButton',
              key:
                this.props.uploadState === TaskState.STARTED ? ModalButtonKey.LOADING : ModalButtonKey.NEXT,
            },
          },
        ])}
        {...this.props.modalProps}
      >
        <label>Import from</label>
        <FormGroup>
          <ImportSourceTypeSelector
            importSourceType={this.props.importSourceType}
            isSdfAllowed={isSdfAllowed}
            onSelect={this.handleChangeImportSource}
          />
        </FormGroup>
        {(() => {
          switch (this.props.importSourceType) {
            case ImportSourceType.RAW_TEXT:
              return (
                <UploadRawTextForm
                  autoFocus={true}
                  exampleImageURL={this.getExampleImageURL()}
                  helpText={`The first row should specify column headers, which must be unique.
                     Each row after that should specify a ${this.props.importItemWord}.`}
                  onTextAreaChange={this.handleOnTextAreaChange}
                  text={this.props.rawText}
                />
              );
            case ImportSourceType.SDF:
              return (
                <UploadSDFForm
                  file={this.props.file}
                  onRemoveFile={this._handleRemoveSpreadsheet}
                  onSelectFile={this._handleUploadAndParseSDF}
                />
              );
            default:
              assert(
                this.props.importSourceType === ImportSourceType.SPREADSHEET,
                'Unexpected import source type.'
              );
              return (
                <UploadSpreadsheetForm
                  exampleImageURL={this.getExampleImageURL()}
                  file={this.props.file}
                  fileWord={this.props.importItemWord}
                  onRemoveFile={this._handleRemoveSpreadsheet}
                  onSelectFile={this._handleUploadAndParseSpreadsheet}
                  maxAllowedRowsToImport={this.props.maxAllowedRowsToImport}
                />
              );
          }
        })()}
        {BenchlingConfig.NAV_ENABLE_LOCALE_AWARE_PARSING_FOR_SPREADSHEET_UPLOAD &&
          this.spreadsheetLocaleSelectorOrText()}
        {this.props.uploadState === TaskState.FAILED && this.props.errorMessage && (
          <div className="u-danger-color">{this.props.errorMessage}</div>
        )}
      </Modal>
    );
  }
}

export default UploadSpreadsheetModalPane;
